import { Box, Divider, Grid, Typography, useTheme } from '@mui/material';
import { Button, Modal, ModalState, Switch } from '@perry-weather/component-library';
import { ArrowBack, Check } from '@mui/icons-material';
import Link from '../Base/Link';
import { HardwareRemoteRelay, RemoteRelay } from '../../Services/API/hardwareApi';
import { PolicyType } from '../../Services/API/policyApi';
import { useToggleRemoteRelayMutation, useGetRemoteRelayStatusesQuery } from '../../Services/API/remoteRelayapi';
import { usePolicyTypesQuery } from '../../Services/API/policyApi';
import { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/dist/query';

type StormbridgeModalProps = {
  modalState: ModalState;
  remoteRelays?: RemoteRelay[];
  ipAddress?: string;
};

type RelayCardProps = {
  isRefreshingRelays: boolean;
  relay: RemoteRelay;
  relayStatus: HardwareRemoteRelay | undefined;
  ipAddress: string;
  policyType: PolicyType | undefined;
};

const RelayCard = ({ isRefreshingRelays, relay, relayStatus, ipAddress, policyType }: RelayCardProps) => {
  const theme = useTheme();
  const [toggleRelay, { isLoading }] = useToggleRemoteRelayMutation();
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const handleOpen = () => setConfirmOpen(true);
  const handleClose = () => setConfirmOpen(false);

  const handleToggle = async (toggled: boolean) => {
    setError(null);
    try {
      const result: any = await toggleRelay({
        ipAddress,
        id: relay.name,
        toggled,
      });

      if (!result.data) {
        setError('Failed to toggle relay, please try again.');
      }
    } catch (error) {
      setError('An unexpected error occurred.');
    }
  };

  const hasAutomation = !!relay.remoteRelayTrigger;
  const labelType = hasAutomation ? 'active' : 'manual';
  const labelStyles = {
    active: {
      background: '#2A433B',
      text: '#5EC269',
    },
    manual: {
      background: 'black',
      text: 'white',
    },
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        padding: '1em 2em',
        borderRadius: 5,
        border: '1px solid #8D94A0',
        display: 'grid',
        alignItems: 'center',
        mb: 2,
        mt: 2,
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant='h6'>{relay.name}</Typography>
          {policyType && <Typography color='text.secondary'>Policy: {policyType.displayName}</Typography>}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              padding: '1px 10px',
              backgroundColor: labelStyles[labelType].background,
              color: labelStyles[labelType].text,
              border: `1px solid ${labelStyles[labelType].text}`,
              borderRadius: 10,
              width: 'fit-content',
            }}>
            {hasAutomation && <Check sx={{ fontSize: 16 }} />}
            <Typography fontSize={12} lineHeight={1}>
              {hasAutomation ? 'Automation Active' : 'No Automation Set'}
            </Typography>
          </Box>
        </Box>

        <Divider orientation='vertical' flexItem sx={{ backgroundColor: '#4c4c4c', mx: 2 }} />

        <Box sx={{ textAlign: 'center', display: 'grid', justifyItems: 'center', gap: 1 }}>
          <Typography>Manual Control</Typography>

          <Switch
            onChange={handleOpen}
            checked={relayStatus?.toggled}
            label={isLoading || isRefreshingRelays ? 'Loading...' : relayStatus?.toggled ? 'ON' : 'OFF'}
            sx={{ switch: { marginLeft: 1 } }}
          />
          <Typography variant='body2' color='text.secondary'>
            {isLoading || isRefreshingRelays ? '' : `Relay is currently ${relayStatus?.toggled ? 'On' : 'Off'}`}
          </Typography>
        </Box>
      </Box>
      {error && (
        <Typography variant='body2' color='error' textAlign='end' mt={1}>
          {error}
        </Typography>
      )}

      <Modal modalState={{ open: confirmOpen, handleOpen, handleClose }}>
        <Box sx={{ padding: 4, borderRadius: 5 }}>
          <Typography variant='h4' textAlign='center' fontWeight='bold' mb={2}>
            Confirm Manual Override
          </Typography>
          <Typography fontSize={18} textAlign='center'>
            Are you sure you want to manually toggle the relay at "{relay.name}"? This may override any active policy
            automation.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} type='clear'>
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleToggle(!relayStatus?.toggled);
                setConfirmOpen(false);
              }}>
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

const StormbridgeModal = ({ modalState, remoteRelays, ipAddress }: StormbridgeModalProps) => {
  const theme = useTheme();
  const {
    data: remoteRelayStatuses,
    isLoading,
    isFetching,
  } = useGetRemoteRelayStatusesQuery(modalState.open ? ipAddress || skipToken : skipToken);
  const { data: policyTypes } = usePolicyTypesQuery();

  const relaysOn = remoteRelayStatuses?.filter(relay => relay.toggled).length ?? 0;
  const relaysOff = remoteRelayStatuses?.filter(relay => !relay.toggled).length ?? 0;
  const pluralizedRelaysOn = relaysOn === 1 ? 'Relay' : 'Relays';
  const pluralizedRelaysOff = relaysOff === 1 ? 'Relay' : 'Relays';

  return (
    <Modal modalState={modalState}>
      <Link onClick={modalState.handleClose} sx={{ mb: 2.5 }}>
        <ArrowBack sx={{ width: 18, height: 18, mr: '5px' }} />
        Back
      </Link>
      <Box>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <Typography variant='h5' fontWeight='bold' lineHeight={1}>
              StormBridge
            </Typography>
            <Typography color={theme.palette.text.secondary}>View and control your relays.</Typography>
            {!isLoading && (
              <Typography mt={2}>
                {relaysOn} {pluralizedRelaysOn} ON / {relaysOff} {pluralizedRelaysOff} OFF
              </Typography>
            )}
          </Grid>
        </Grid>
        <Box>
          {remoteRelays?.map(relay => {
            const relayStatus = remoteRelayStatuses?.find(status => status.id === relay.name);
            const policyType = policyTypes?.find(
              (type: { id: number | undefined }) => type.id === relay.remoteRelayTrigger?.policyTypeId
            );
            return (
              <RelayCard
                key={relay.id}
                relay={relay}
                relayStatus={relayStatus}
                ipAddress={ipAddress!}
                isRefreshingRelays={isFetching}
                policyType={policyType}
              />
            );
          })}
          {remoteRelays?.length === 0 && (
            <Typography variant='body2' sx={{ mt: 4 }}>
              No relays found
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default StormbridgeModal;
